/**
 * One intent = one `Idempotency-Key` (#1076-b, vingapi#2468 S1).
 *
 * An intent is "this person sends this exact body to this endpoint". The key
 * lives from the first attempt until the server answers 2xx for it (or tells us
 * the key itself was wrong: `idempotency_key_reused` / `_invalid`). Every repeat
 * of the same body before then (a second press after a status-0 or 5xx, two
 * concurrent calls) carries the same key, so the server replays the stored
 * answer instead of sending a second time. A different body is a new intent.
 *
 * The registry lives in sessionStorage (survives a reload in this tab; another
 * tab does not share it) with an in-memory fallback when storage is unusable.
 * Entries older than 24 h are ignored.
 */

import { newRandomHexId } from '@/lib/traceContext';

const STORAGE_KEY = 'ving.idempotencyKeys';

export const IDEMPOTENCY_KEY_TTL_MS = 24 * 60 * 60 * 1000;

interface Entry {
  key: string;
  at: number;
}

const memory = new Map<string, Entry>();

function readStored(): Record<string, Entry> | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, Entry>) : {};
  } catch {
    return null;
  }
}

function writeStored(all: Record<string, Entry>): boolean {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    return true;
  } catch {
    return false;
  }
}

/** Sorted keys at every level, `undefined` dropped, array order kept, compact separators. */
function canonicalize(value: unknown): string | undefined {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  const withToJson = value as { toJSON?: () => unknown };
  if (typeof withToJson.toJSON === 'function') return canonicalize(withToJson.toJSON());
  if (Array.isArray(value)) return `[${value.map((v) => canonicalize(v) ?? 'null').join(',')}]`;
  const obj = value as Record<string, unknown>;
  const parts: string[] = [];
  for (const k of Object.keys(obj).sort()) {
    const c = canonicalize(obj[k]);
    if (c !== undefined) parts.push(`${JSON.stringify(k)}:${c}`);
  }
  return `{${parts.join(',')}}`;
}

/**
 * What the server hashes: the body as sent on the wire (snake_case keys, the
 * caller converts first), equal to Python
 * `json.dumps(body, sort_keys=True, separators=(",", ":"), ensure_ascii=False)`.
 */
export function canonicalBody(wireBody: unknown): string {
  return canonicalize(wireBody) ?? 'null';
}

/** 32 hex chars. Not crypto.randomUUID: it is above the browser floor (#981). */
function newKey(): string {
  return newRandomHexId();
}

export interface Intent {
  key: string;
  /** The intent is over (2xx, or the server refused the key itself): the next press starts fresh. */
  release: () => void;
}

/** The key for `(endpoint, wireBody)`: the live one if this intent is open, else a new one. */
export function acquireIdempotencyKey(endpoint: string, wireBody: unknown): Intent {
  const id = `${endpoint}\n${canonicalBody(wireBody)}`;
  const now = Date.now();
  const stored = readStored();
  const live = (e: Entry | undefined): e is Entry => !!e && now - e.at < IDEMPOTENCY_KEY_TTL_MS;

  let entry: Entry | undefined = stored ? stored[id] : memory.get(id);
  if (!live(entry)) {
    entry = { key: newKey(), at: now };
    if (stored) {
      for (const k of Object.keys(stored)) if (!live(stored[k])) delete stored[k];
      stored[id] = entry;
      if (!writeStored(stored)) memory.set(id, entry);
    } else {
      memory.set(id, entry);
    }
  }
  const key = entry.key;
  return {
    key,
    release: () => {
      const cur = readStored();
      if (cur && cur[id]?.key === key) {
        delete cur[id];
        writeStored(cur);
      }
      if (memory.get(id)?.key === key) memory.delete(id);
    },
  };
}

/** `detail.code` of a vingapi#2468 error body (`{"detail":{"code":…}}`), else undefined. */
export function idempotencyErrorCode(error: unknown): string | undefined {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  const code = (detail as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
}

/** Test seam: forget every entry. */
export function resetIdempotencyKeys(): void {
  memory.clear();
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* unusable storage: nothing to clear */
  }
}
